import type { DagEdge } from "@/api/dag";
import type { LifecycleStateLabel } from "@/api/projects";

/**
 * **The phase lane (VIS/V2): a predetermined flow read as bands of repetition
 * rather than as a graph.**
 *
 * `full_affdo_sequential` is fifteen nodes and thirty-seven edges. Nobody
 * decides that topology at submit time — it is a fixed template shipped as
 * YAML — and at the zoom that fits it in a panel the labels are gone, so what
 * the reader gets from the picture is a tangle. What they actually want is the
 * order of the work and how wide each part of it goes.
 *
 * That IS in the graph, and this module extracts it. **Nothing here is
 * invented.** The artboard (`dev/design/flowstack-workspace/Submit.dc.html`)
 * titles its stages *prepare · enumerate · scan · refine · fit · merge*, and
 * those words are not in the record: no field says a node is a "refine" step,
 * and writing the vocabulary in would be a claim about somebody's flow that no
 * data backs — `VIS/V8`'s defect (#1240) in a new place. So every band here is
 * a contiguous run of the flow's own nodes, every band's label is built from
 * node names the graph already carries, and a graph whose bands cannot be
 * derived yields `null` rather than a guess.
 *
 * **It is narrow by construction and it does not replace the graph.** A flow
 * the user is *composing* is the opposite case — there the graph IS the
 * artifact — so this is a reading aid for flows nobody authored, the graph
 * stays one press away, and it still expands to the whole window.
 *
 * ## What a band is
 *
 * A **fan-out scope chain**: the ordered list of nodes whose output a step
 * repeats over. Steps that share a chain AND sit next to each other in reading
 * order form one band. For the sequential AFFDO template that is:
 *
 * | band | chain                          | steps                                      |
 * |------|--------------------------------|--------------------------------------------|
 * | 1    | (none — runs once)             | `fragment`, `ligand_topology`              |
 * | 2    | `fragment`                     | `fragment_topology` … `torsion_select`     |
 * | 3    | `fragment` × `torsion_select`  | `torsion_xtb_scan` … `dft_constrained_opt` |
 * | 4    | `fragment`                     | `gaff_evaluation`, `torsion_fit`           |
 * | 5    | (none — runs once)             | `merge_fragments`                          |
 *
 * Five bands, arrived at from the graph instead of from a wordlist, and the
 * artboard's shape. Bands 2 and 4 share a chain and are still two bands,
 * because they are not contiguous: the flow descends into the torsion scope
 * between them and comes back out, and merging them would claim an ordering the
 * flow does not have.
 *
 * **Why not one band per topological layer.** Because this flow is fourteen
 * layers deep with one node in nearly all of them, so that lane is fourteen
 * columns of truncated identifiers — the thumbnail's problem wearing a
 * different shape. The fan-out regime is the thing that actually changes phase.
 *
 * ## How the chain is derived
 *
 * `fanout_kind` says a node fans out but not over WHAT. The edge list says: the
 * backend marks exactly one incoming edge `kind: "fanout"`, and that edge's
 * source is the node the fan-out is anchored to (`_node_upstream_and_fanout`,
 * `src/flowstack/server/routes/dag.py`). So, walking in reading order:
 *
 * - `fanout_kind === null` → the chain is empty; the step runs once per flow;
 * - `"scope"` → the step opens a NEW scope over its fanout source `P`, so its
 *   chain is `P`'s chain plus `P`;
 * - `"match"` → the step JOINS `P`'s scope, so it takes `P`'s chain unchanged.
 *
 * This is what lets the lane say *for each `fragment` × `torsion_select`* — the
 * fan-out's SHAPE — where `fanout_kind` alone could only have said "fanned".
 *
 * ## When it declines to answer
 *
 * `derivePhaseLane` returns `null`, and the surface falls back to the graph,
 * when:
 *
 * - the graph has a **cycle** — there is no reading order to lay out;
 * - a fanned step has **no fanout edge**, or two different ones. A `scope`
 *   whose `over:` path resolves to a platform binding (`$.inputs.x`) rather
 *   than to a node produces no fanout edge, and there is then no way to tell a
 *   fan over a flow input (chain depth 1) from one nested inside another scope.
 *   Both are representable; the data does not say which, so this says nothing;
 * - there is only **one band**. A flow with no fan-out has no phases to read,
 *   and its graph is a chain that renders perfectly well — a lane there would
 *   be a second way to look at the same list.
 *
 * That last case is why this returns `PhaseLane | null` rather than always
 * answering: "there is a lane" is precisely "the lane says something the graph
 * does not".
 */

/**
 * The subset of a DAG node this reads.
 *
 * Structural, and optional in its last two fields, so all three wire shapes
 * satisfy it without an adapter: `DagNode` (the submit page's structural
 * preview, no run at all), `DagNodeWithState` (its run-state overlay: state,
 * no counts) and `FlowRunNodeState` (the run workspace's cohort: both).
 */
export interface PhaseLaneNode {
  name: string;
  fanout_kind: "scope" | "match" | null;
  /** This node's run state, on the shapes that have been dispatched. */
  status?: LifecycleStateLabel | null;
  /** The run-scoped sibling count. Absent on both template endpoints. */
  child_count?: number;
}

export interface PhaseLaneStep {
  name: string;
  status: LifecycleStateLabel | null;
  /**
   * The MEASURED number of sibling runs this step expanded into, and `null`
   * whenever there is no measurement to report.
   *
   * `null` covers three genuinely different things — a template that has never
   * run, a step nothing has reached, and a step that ran exactly once — and
   * they collapse on purpose: none of them is a fan-out width, and a width is
   * the one number this field is allowed to carry. A width is a property of the
   * ligand a flow fans over, not of the flow, so a template preview HAS none.
   * `child_count: 0` is ambiguous at the source besides (not reached / empty
   * source list / expansion crashed — `FlowRunNodeState.expansion_reason`), and
   * drawing it as "×0" would turn that ambiguity into an assertion. The graph
   * and the node table under it are where those states are told apart.
   */
  width: number | null;
}

export interface PhaseLaneBand {
  /**
   * The fan-out sources this band repeats over, outermost first. Empty means
   * the band runs once per flow.
   */
  over: string[];
  steps: PhaseLaneStep[];
}

export interface PhaseLane {
  bands: PhaseLaneBand[];
  /** The graph this was read from, so a surface can say what it summarised. */
  nodeCount: number;
  edgeCount: number;
}

/** See the module docstring — including every case that returns `null`. */
export function derivePhaseLane(
  nodes: readonly PhaseLaneNode[],
  edges: readonly DagEdge[]
): PhaseLane | null {
  if (nodes.length === 0) return null;
  const byName = new Map(nodes.map((n) => [n.name, n] as const));

  const dependencies = new Map<string, string[]>();
  const fanoutSource = new Map<string, string>();
  for (const edge of edges) {
    // An edge naming a node this response does not carry is not a dependency
    // anything here can resolve. The backend already filters bindings to
    // declared nodes, so this guards a partial payload rather than a shape the
    // API produces.
    if (!byName.has(edge.source) || !byName.has(edge.target)) continue;
    const existing = dependencies.get(edge.target);
    if (existing === undefined) dependencies.set(edge.target, [edge.source]);
    else existing.push(edge.source);
    if (edge.kind !== "fanout") continue;
    const claimed = fanoutSource.get(edge.target);
    // Two different anchors for one step is two different scopes, and picking
    // either would be a coin toss dressed as a reading.
    if (claimed !== undefined && claimed !== edge.source) return null;
    fanoutSource.set(edge.target, edge.source);
  }

  const order = readingOrder(nodes, dependencies);
  if (order === null) return null;

  const chains = new Map<string, string[]>();
  for (const name of order) {
    // `order` is built from `nodes`, so the lookup cannot miss.
    const kind = byName.get(name)!.fanout_kind;
    if (kind === null) {
      chains.set(name, []);
      continue;
    }
    const source = fanoutSource.get(name);
    if (source === undefined) return null;
    const sourceChain = chains.get(source);
    // The anchor is an upstream, so it precedes this node in `order`; an
    // unresolved chain here would mean the node list and the edge list
    // disagreed about who exists.
    if (sourceChain === undefined) return null;
    chains.set(name, kind === "scope" ? [...sourceChain, source] : sourceChain);
  }

  const bands: PhaseLaneBand[] = [];
  for (const name of order) {
    const node = byName.get(name)!;
    const chain = chains.get(name)!;
    const step: PhaseLaneStep = {
      name,
      status: node.status ?? null,
      width: node.child_count !== undefined && node.child_count > 1 ? node.child_count : null,
    };
    const open = bands[bands.length - 1];
    if (open !== undefined && sameChain(open.over, chain)) open.steps.push(step);
    else bands.push({ over: chain, steps: [step] });
  }
  if (bands.length < 2) return null;

  return { bands, nodeCount: nodes.length, edgeCount: edges.length };
}

function sameChain(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((name, i) => name === b[i]);
}

/**
 * The order the lane reads in: dependency order, breaking every remaining tie
 * toward the order the flow declared its nodes in.
 *
 * The tie-break is the reason this is written out rather than handed to a
 * queue. A Flow template is authored top to bottom in the order its author
 * thought the work happens, and that authored order is the closest thing the
 * data has to editorial intent about phases — so wherever the graph permits it,
 * the lane keeps it. A generic topological sort is equally correct and scatters
 * the bands, which for a READING AID is the failure that matters.
 *
 * `null` on a cycle: the scan finds no node whose dependencies are all placed,
 * and a lane must not claim an order that does not exist. (The Flow compiler
 * rejects a cyclic template, so this guards a malformed payload rather than a
 * shape the product produces.)
 */
function readingOrder(
  nodes: readonly PhaseLaneNode[],
  dependencies: ReadonlyMap<string, string[]>
): string[] | null {
  const placed = new Set<string>();
  const order: string[] = [];
  const pending = nodes.map((n) => n.name);
  while (pending.length > 0) {
    const next = pending.findIndex((name) =>
      (dependencies.get(name) ?? []).every((dep) => placed.has(dep))
    );
    if (next === -1) return null;
    const [name] = pending.splice(next, 1);
    placed.add(name);
    order.push(name);
  }
  return order;
}
